<template>
  <button
    v-if="canEditReferrer"
    class="btn btn-sm btn-outline-secondary"
    data-test="edit-commission"
    @click="editCommission"
  >
    Edit
  </button>

  <!-- Edit Commission Modal -->
  <div class="modal fade" :id="modalId" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Edit Commission Settings</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <LoadingIndicatorComponent v-if="loadingCommissionEdit" />
          <div v-else>
            <form @submit.prevent="saveCommissionSettings">
              <div class="mb-3">
                <label class="form-label">Sales Partner Type</label>
                <select
                  class="form-select"
                  v-model="editedCommission.sales_partner_type"
                >
                  <option value="">Standard Referrer</option>
                  <option value="sales_partner">Sales Partner</option>
                  <option value="permanent">Permanent Partner</option>
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label">Commission Rate (%)</label>
                <input
                  type="number"
                  class="form-control"
                  data-test="commission-rate"
                  v-model="editedCommission.rate"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>

              <div class="mb-3">
                <label class="form-label">Duration (years)</label>
                <input
                  type="number"
                  class="form-control"
                  v-model="editedCommission.duration_years"
                  min="0"
                  step="0.5"
                />
                <small class="form-text text-muted"
                  >Set to 0 for indefinite duration</small
                >
              </div>

              <div class="mb-3">
                <label class="form-label" for="commissionExpiryDate"
                  >Expiry Date</label
                >
                <input
                  id="commissionExpiryDate"
                  type="date"
                  class="form-control"
                  v-model="editedCommission.expiry_date"
                />
                <small class="form-text text-muted"
                  >Hard end date for the referral commission — overrides the
                  duration. Leave empty to use the duration.</small
                >
              </div>

              <div class="mb-3">
                <label class="form-label">Year 2 Commission Rate (%)</label>
                <input
                  type="number"
                  class="form-control"
                  v-model="editedCommission.year_2_rate"
                  min="0"
                  max="100"
                  step="0.1"
                />
                <small class="form-text text-muted"
                  >Rate applied during the second year (if applicable)</small
                >
              </div>

              <div class="mb-3">
                <label class="form-label">Permanent Rate (%)</label>
                <input
                  type="number"
                  class="form-control"
                  v-model="editedCommission.permanent_rate"
                  min="0"
                  max="100"
                  step="0.1"
                />
                <small class="form-text text-muted"
                  >Rate applied after initial duration period</small
                >
              </div>

              <div class="mb-3">
                <label class="form-label">Fixed price for commission</label>
                <input
                  type="number"
                  class="form-control"
                  v-model="editedCommission.special_price"
                  min="0"
                  step="0.1"
                />
                <small class="form-text text-muted"
                  >Price used for commission calculations</small
                >
              </div>

              <div class="mb-3 form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="noCashPayoutCheck"
                  v-model="noCashPayout"
                />
                <label class="form-check-label" for="noCashPayoutCheck">
                  No Cash Payout (Only Options)
                </label>
              </div>

              <h6 class="mt-4">Manager</h6>
              <div class="mb-3">
                <label class="form-label" :for="`${managerFieldId}-company`"
                  >Manager</label
                >
                <select
                  :id="`${managerFieldId}-company`"
                  data-test="manager-company"
                  class="form-select"
                  v-model="editedCommission.manager_company_id"
                >
                  <option :value="null">No manager</option>
                  <option
                    v-for="candidate in selectableManagers"
                    :key="candidate.value"
                    :value="candidate.value"
                  >
                    {{ candidate.label }}
                  </option>
                </select>
                <small class="form-text text-muted"
                  >Earns a share of this partner's referral bonuses, deducted
                  from the partner's bonus</small
                >
              </div>
              <template v-if="editedCommission.manager_company_id !== null">
                <div class="mb-3">
                  <label class="form-label" :for="`${managerFieldId}-rate`"
                    >Manager share (%)</label
                  >
                  <input
                    :id="`${managerFieldId}-rate`"
                    data-test="manager-rate"
                    type="number"
                    class="form-control"
                    v-model="editedCommission.manager_rate"
                    min="0.01"
                    max="100"
                    step="0.01"
                  />
                </div>
                <div
                  v-for="source in ALL_MANAGER_SOURCES"
                  :key="source"
                  class="mb-2 form-check"
                >
                  <input
                    :id="`${managerFieldId}-${source}`"
                    :data-test="`manager-source-${source}`"
                    type="checkbox"
                    class="form-check-input"
                    :value="source"
                    v-model="editedCommission.manager_applies_to"
                  />
                  <label
                    class="form-check-label"
                    :for="`${managerFieldId}-${source}`"
                    >{{ MANAGER_SOURCE_LABELS[source] }}</label
                  >
                </div>
              </template>
            </form>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            data-test="save-commission"
            @click="saveCommissionSettings"
            :disabled="loadingCommissionEdit || !managerSettingsValid"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { ref, computed, inject, onMounted, useId, watch } from "vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { Modal } from "bootstrap";
import { ADMIN_IDS } from "@/constants";
import { round } from "lodash-es";
import type { SwapReferrerOption } from "@/composables/useSwapReferrers";
import { CommissionConfig, ManagerShareSource } from "@/types";

/**
 * Commission Configuration Attributes Map:
 *
 * - rate: Decimal - The commission rate (0.0-1.0, e.g., 0.3 for 30%)
 * - duration_years: Number - Duration of the commission period in years (null or 0 for indefinite)
 * - year_2_rate: Decimal - Rate applied during the second year (0.0-1.0)
 * - permanent_rate: Decimal - Rate applied after the initial duration period (0.0-1.0)
 * - special_price: Number - Multiplier for special price calculations
 * - special_condition: String - Special conditions like "no_cash_payout" for options-only
 *
 * Example configurations from the codebase:
 *
 * Standard referrer:
 * {
 *   rate: 0.3,
 *   duration_years: 1
 * }
 *
 * Sales partner with permanent rate:
 * {
 *   rate: 0.2,
 *   duration_years: 1,
 *   special_price: 2.0,
 *   permanent_rate: 0.1
 * }
 *
 * Sales partner with year 2 rate and permanent rate:
 * {
 *   rate: 0.2,           // 20% for first year
 *   duration_years: 1,
 *   year_2_rate: 0.15,   // 15% for second year
 *   permanent_rate: 0.1, // 10% for third year onwards
 *   special_price: 2.0
 * }
 *
 * Sales partner with no cash payout:
 * {
 *   rate: 0.2,
 *   duration_years: 1,
 *   special_price: 2.0,
 *   special_condition: 'no_cash_payout'
 * }
 */

// Define the referrer interface
interface Referrer {
  company_id: number;
  company_name: string;
  sales_partner_type: string;
  commission_rate: number;
  commission_duration_years: number;
  commission_config?: CommissionConfig;
  referred_companies_count: number;
  total_power_kw: number;
  projected_production_mwh: number;
  owner_account_ids?: number[];
}

// Fraction, as the backend stores it; the form edits it as a percentage.
const DEFAULT_MANAGER_RATE = 0.05;

// Rounded so float noise (0.07 * 100) doesn't show up in the input or the saved rate.
const managerRateToPercent = (rate: number) => round(rate * 100, 4);
const managerRateFromPercent = (percent: number) => round(percent / 100, 6);

const ALL_MANAGER_SOURCES: ManagerShareSource[] = [
  "SALE_REFERRAL",
  "SWAP_REFERRAL",
  "FEE_REFERRAL",
];

const MANAGER_SOURCE_LABELS: Record<ManagerShareSource, string> = {
  SALE_REFERRAL: "Sale referral bonus",
  SWAP_REFERRAL: "Swap buy bonus",
  FEE_REFERRAL: "Fee / flat-fee bonus",
};

// Props
const props = defineProps<{
  referrer: Referrer;
  managerCandidates: SwapReferrerOption[];
  accountData: {
    admin_account_id: number | null;
  };
}>();

// Emits
const emit = defineEmits<{
  updated: [];
}>();

// Get global error handler
const globalErrorHandler = inject("globalErrorHandler") as (error: any) => void;

// Access control computed property
const isAdmin = computed(() => {
  return (
    props.accountData.admin_account_id &&
    ADMIN_IDS.FULL_ACCESS.includes(props.accountData.admin_account_id)
  );
});

// Check if user can edit a specific referrer
const canEditReferrer = computed(() => {
  // Only admins can edit
  return isAdmin.value;
});

// State
const loadingCommissionEdit = ref(false);

// Define the type for the edited commission form
interface EditedCommissionForm {
  sales_partner_type: string;
  rate: number;
  duration_years: number;
  year_2_rate: number;
  permanent_rate: number;
  special_price: number;
  expiry_date: string; // ISO date; "" = no expiry (use the duration)
  manager_company_id: number | null;
  manager_rate: number | string; // percentage; "" while the admin clears the input
  manager_applies_to: ManagerShareSource[];
}

const editedCommission = ref<EditedCommissionForm>({
  sales_partner_type: "",
  rate: 30,
  duration_years: 1,
  year_2_rate: 0,
  permanent_rate: 0,
  special_price: 0,
  expiry_date: "",
  manager_company_id: null,
  manager_rate: managerRateToPercent(DEFAULT_MANAGER_RATE),
  manager_applies_to: [...ALL_MANAGER_SOURCES],
});

// Special condition checkbox
const noCashPayout = ref(false);

// Watch for changes to noCashPayout
watch(noCashPayout, (newValue) => {
  // When checkbox is toggled, update the UI accordingly
  if (newValue) {
    // If no cash payout is selected, disable rate field
    editedCommission.value.rate = 0;
  }
});

// A stored manager missing from the candidates (e.g. soft-deleted) stays visible so the
// select doesn't read "No manager" while the id is kept
const selectableManagers = computed(() => {
  const candidates = props.managerCandidates.filter(
    (candidate) => candidate.value !== props.referrer.company_id
  );
  const storedId = props.referrer.commission_config?.manager?.company_id;
  const storedIsMissing =
    storedId !== undefined &&
    !candidates.some((candidate) => candidate.value === storedId);
  return storedIsMissing
    ? [...candidates, { value: storedId, label: `Company #${storedId}` }]
    : candidates;
});

// The backend replaces the stored config wholesale and rejects an incomplete manager
const managerSettingsValid = computed(
  () =>
    editedCommission.value.manager_company_id === null ||
    (Number(editedCommission.value.manager_rate) > 0 &&
      Number(editedCommission.value.manager_rate) <= 100 &&
      editedCommission.value.manager_applies_to.length > 0)
);

const managerFieldId = useId();

// Computed modal ID to ensure uniqueness
const modalId = computed(
  () => `editCommissionModal_${props.referrer.company_id}`
);

// Methods
const editCommission = async () => {
  // Only admins can edit commission settings
  if (!isAdmin.value) return;

  loadingCommissionEdit.value = true;

  try {
    // Get the commission config from the referrer
    const commissionConfig =
      props.referrer.commission_config || ({} as CommissionConfig);

    // Set the edited commission values from the referrer
    editedCommission.value = {
      sales_partner_type: props.referrer.sales_partner_type || "",
      rate: props.referrer.commission_rate * 100, // Convert to percentage
      duration_years: props.referrer.commission_duration_years || 1,
      year_2_rate: (commissionConfig.year_2_rate || 0) * 100, // Convert to percentage
      permanent_rate: (commissionConfig.permanent_rate || 0) * 100, // Convert to percentage
      special_price: commissionConfig.special_price || 0,
      expiry_date: commissionConfig.expiry_date || "",
      manager_company_id: commissionConfig.manager?.company_id ?? null,
      manager_rate: managerRateToPercent(
        commissionConfig.manager?.rate ?? DEFAULT_MANAGER_RATE
      ),
      manager_applies_to: commissionConfig.manager?.applies_to ?? [
        ...ALL_MANAGER_SOURCES,
      ],
    };

    // Set the special condition checkbox
    noCashPayout.value =
      commissionConfig.special_condition === "no_cash_payout";

    // Show modal
    const modal = new Modal(
      document.getElementById(modalId.value) as HTMLElement
    );
    modal.show();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    loadingCommissionEdit.value = false;
  }
};

const saveCommissionSettings = async () => {
  // Only admins can save commission settings
  if (!isAdmin.value) return;

  loadingCommissionEdit.value = true;

  try {
    // Create the complete commission config object at once
    const commissionConfig: CommissionConfig = {
      rate: editedCommission.value.rate / 100, // Convert back to decimal
      duration_years:
        editedCommission.value.duration_years === 0
          ? null
          : editedCommission.value.duration_years,
      year_2_rate:
        editedCommission.value.year_2_rate > 0
          ? editedCommission.value.year_2_rate / 100
          : undefined,
      permanent_rate:
        editedCommission.value.permanent_rate > 0
          ? editedCommission.value.permanent_rate / 100
          : undefined,
      special_price:
        editedCommission.value.special_price > 0
          ? editedCommission.value.special_price
          : undefined,
      special_condition: noCashPayout.value ? "no_cash_payout" : undefined,
      expiry_date: editedCommission.value.expiry_date || undefined,
      manager:
        editedCommission.value.manager_company_id === null
          ? undefined
          : {
              company_id: editedCommission.value.manager_company_id,
              rate: managerRateFromPercent(
                Number(editedCommission.value.manager_rate)
              ),
              applies_to: editedCommission.value.manager_applies_to,
            },
    };

    await goSolidApi.post(
      `/api/admin/commission/update/${props.referrer.company_id}`,
      {
        commission_config: commissionConfig,
        sales_partner_type: editedCommission.value.sales_partner_type,
      }
    );

    // Close modal
    const modal = Modal.getInstance(
      document.getElementById(modalId.value) as HTMLElement
    ) as Modal;
    modal.hide();

    // Emit event to refresh dashboard data
    emit("updated");

    // Force full page reload to update all commission displays
    window.location.reload();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    loadingCommissionEdit.value = false;
  }
};

// Lifecycle hooks
onMounted(() => {
  // No initialization needed
});
</script>
